import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { worldUV } from './shapes';

interface Item { geo: THREE.BufferGeometry; matrix: THREE.Matrix4 }
interface Bucket { mat: THREE.Material; cast: boolean; uvScale: number; items: Item[] }

const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _e = new THREE.Euler(), _s = new THREE.Vector3(), _p = new THREE.Vector3();

/**
 * Collects static meshes and merges them per (material, castShadow) into a
 * handful of draw calls. The scene has hundreds of small props; this turns
 * them into ~20 draws.
 */
export class StaticBatch {
  private buckets = new Map<string, Bucket>();
  /** Materials whose tiled maps want world-space UVs (k = texels per unit). */
  private worldUV = new Map<THREE.Material, number>();

  useWorldUV(mat: THREE.Material, k: number) { this.worldUV.set(mat, k); }

  add(geo: THREE.BufferGeometry, mat: THREE.Material, x = 0, y = 0, z = 0, opts: { cast?: boolean; rot?: [number, number, number]; scale?: number | [number, number, number] } = {}) {
    _p.set(x, y, z);
    _q.setFromEuler(_e.set(...(opts.rot ?? [0, 0, 0])));
    const sc = opts.scale ?? 1;
    if (typeof sc === 'number') _s.setScalar(sc); else _s.set(...sc);
    this.addMatrix(geo, mat, _m.compose(_p, _q, _s), opts.cast ?? true);
  }

  addMatrix(geo: THREE.BufferGeometry, mat: THREE.Material, matrix: THREE.Matrix4, cast = true) {
    const key = mat.uuid + (cast ? ':c' : ':n');
    let b = this.buckets.get(key);
    if (!b) this.buckets.set(key, (b = { mat, cast, uvScale: this.worldUV.get(mat) ?? 0, items: [] }));
    b.items.push({ geo, matrix: matrix.clone() });
  }

  /** Plain unlit colours (strips, insets) differ only by colour: they share one vertex-coloured material. */
  private static flat(m: THREE.Material): m is THREE.MeshBasicMaterial {
    const b = m as THREE.MeshBasicMaterial;
    return !!b.isMeshBasicMaterial && !b.map && !b.transparent && !b.vertexColors && b.side === THREE.FrontSide && b.blending === THREE.NormalBlending
      && b.onBeforeCompile === THREE.Material.prototype.onBeforeCompile;
  }
  private readonly flatMat = new THREE.MeshBasicMaterial({ name: 'flatColours', vertexColors: true });

  build(parent: THREE.Object3D): THREE.Mesh[] {
    const out: THREE.Mesh[] = [];
    // fold the flat-colour buckets into one per shadow flag, the colour baked per vertex
    for (const [key, b] of [...this.buckets]) {
      if (!StaticBatch.flat(b.mat)) continue;
      const target = this.flatMat.uuid + (b.cast ? ':c' : ':n');
      let into = this.buckets.get(target);
      if (!into) this.buckets.set(target, (into = { mat: this.flatMat, cast: b.cast, uvScale: 0, items: [] }));
      const c = b.mat.color;
      for (const it of b.items) {
        const g = it.geo.index ? it.geo.toNonIndexed() : it.geo.clone(), n = g.attributes.position.count, col = new Float32Array(n * 3);
        for (let i = 0; i < n; i++) col.set([c.r, c.g, c.b], i * 3);
        g.setAttribute('color', new THREE.BufferAttribute(col, 3));
        into.items.push({ geo: g, matrix: it.matrix });
      }
      this.buckets.delete(key);
    }
    for (const b of this.buckets.values()) {
      const geos = b.items.map(({ geo, matrix }) => {
        // Indexed: shared vertices are transformed once (main and shadow passes) instead of once per triangle
        // corner. Normal-less pieces are faceted first, as before, then indexed 1:1.
        let g = geo.attributes.normal ? geo.clone() : (geo.index ? geo.toNonIndexed() : geo.clone());
        g.applyMatrix4(matrix);
        for (const name of Object.keys(g.attributes)) if (name !== 'position' && name !== 'normal' && name !== 'uv' && !(name === 'color' && b.mat === this.flatMat)) g.deleteAttribute(name);
        if (!g.attributes.normal) g.computeVertexNormals();
        if (!g.index) g.setIndex(Array.from({ length: g.attributes.position.count }, (_, i) => i));
        if (b.uvScale) worldUV(g, b.uvScale);
        else if (!g.attributes.uv) g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(g.attributes.position.count * 2), 2));
        g.clearGroups();
        return g;
      });
      const merged = mergeGeometries(geos, false);
      geos.forEach((g) => g.dispose());
      if (!merged) continue;
      merged.computeBoundingSphere();
      const mesh = new THREE.Mesh(merged, b.mat);
      mesh.castShadow = b.cast;
      mesh.receiveShadow = true;
      mesh.matrixAutoUpdate = false;
      parent.add(mesh);
      out.push(mesh);
    }
    this.buckets.clear();
    return out;
  }
}
